// Copyright (c) 2023 embyt GmbH. All rights reserved.
// Author: Roman Morawek <roman.morawek@embyt.com>

import { ITariffCircuit, IEnergyMeter, EnergySource } from "parameter";
import { ITrendData, ITrendDataRow } from "states/trendingstates";
import { roundTimestamp } from "guiutils";
import { isSet } from "utils";
import {
  IDropdownSelectOption,
  SELECT_ALL_OPTION_ITEMS,
  allOption,
} from "view/comp/DropdownSelect";

const DEFAULT_ENERGY_SOURCE = "electrical";

class TariffCircuitList {
  public readonly tariffCircuits;
  public readonly energyMeters;

  constructor(tariffCircuits: ITariffCircuit[], energyMeters: IEnergyMeter[]) {
    this.tariffCircuits = tariffCircuits;
    this.energyMeters = energyMeters;
  }

  private getEnergyMeter(tlIndex: number) {
    const energyMeter = this.energyMeters.find(
      (curItem) => curItem.id === this.tariffCircuits[tlIndex].contributions?.[0]?.em,
    );
    return energyMeter;
  }

  public getEnergySources(allowAllSources: boolean) {
    const sourcesList = this.tariffCircuits
      .filter((curItem) => curItem.webActive)
      .map(
        (curItem) =>
          this.energyMeters.find((curEm) => curEm.id === curItem.contributions?.[0]?.em)
            ?.energySource ?? DEFAULT_ENERGY_SOURCE,
      );
    const sources = Array.from(new Set(sourcesList));
    return allowAllSources ? [undefined, ...sources] : sources;
  }

  public getCategories(
    energySource: EnergySource | undefined,
    allowAllSources: boolean,
    selectedHierarchyId: number | null | undefined,
    allowAllHierarchies: boolean,
    includeChildren: boolean,
  ) {
    const categories: (string | undefined | null)[] = [];

    const allTariffCircuitIndexes = this.getTariffCircuits(
      energySource,
      allowAllSources,
      selectedHierarchyId,
      allowAllHierarchies,
      undefined,
      includeChildren,
    );
    allTariffCircuitIndexes
      .filter(
        (curItem) =>
          isSet(this.tariffCircuits[curItem].categories) &&
          ((this.tariffCircuits[curItem].categories?.length ?? 0) > 1 ||
            this.tariffCircuits[curItem].categories?.[0] !== ""),
      )
      .every((curItem) =>
        this.tariffCircuits[curItem].categories?.every((cat) => categories.push(cat)),
      );

    const sortedCategories = Array.from(new Set(categories)).sort((a, b) =>
      isSet(a) && isSet(b) ? a.localeCompare(b) : isSet(a) ? 1 : -1,
    );

    return [
      undefined, // all categories
      ...sortedCategories,
      null, // unmapped categories
    ];
  }

  public getHierarchyIds(
    energySource: EnergySource | undefined,
    allowAllSources: boolean,
    allowAllHierarchies: boolean,
  ) {
    const allTariffCircuitIndexes = this.getTariffCircuits(
      energySource,
      allowAllSources,
      undefined,
      allowAllHierarchies,
      undefined,
      false,
    );
    const parentIds: (number | undefined | null)[] = allTariffCircuitIndexes
      .filter((curItem) => isSet(this.tariffCircuits[curItem].parent))
      .map((curItem) => this.tariffCircuits[curItem].parent);
    parentIds.push(null);
    const sortedIds = Array.from(new Set(parentIds)).sort((a, b) => {
      const tcA = this.tariffCircuits.find((curItem, curIndex) => (curItem.id ?? curIndex) === a);
      const tcB = this.tariffCircuits.find((curItem, curIndex) => (curItem.id ?? curIndex) === a);
      return isSet(tcA) && isSet(tcB) ? tcA.name.localeCompare(tcB.name) : isSet(tcA) ? 1 : -1;
    });
    return [undefined, ...sortedIds];
  }

  // gets the list of tariff circuit indexes (not ids!) according seletion criteria
  public getTariffCircuits(
    selectedEnergySource: EnergySource | undefined,
    allowAllSources: boolean,
    selectedHierarchyId: number | null | undefined,
    allowAllHierarchies: boolean,
    selectedCategory: string | null | undefined,
    showAllChildren: boolean,
  ) {
    // check if it is allowed to select all energy sources
    const energySource =
      selectedEnergySource ??
      (allowAllSources ? undefined : this.energyMeters[0]?.energySource ?? DEFAULT_ENERGY_SOURCE);
    // get list of used tariff circuits
    const allList = Array.from(Array(this.tariffCircuits.length).keys());
    const filteredList = allList.filter((curIndex) => {
      const isIdInAnyParent = this.hasIdInAnyParent(
        selectedHierarchyId,
        this.tariffCircuits[curIndex],
      );
      const showTariffCircuit =
        this.tariffCircuits[curIndex].webActive &&
        (!isSet(energySource) ||
          (this.getEnergyMeter(curIndex)?.energySource ?? DEFAULT_ENERGY_SOURCE) ===
            energySource) &&
        ((!isSet(selectedHierarchyId) && (isIdInAnyParent || allowAllHierarchies)) ||
          (showAllChildren === false &&
            (allowAllHierarchies ||
              this.tariffCircuits[curIndex].parent === selectedHierarchyId ||
              (this.tariffCircuits[curIndex].parent === null &&
                selectedHierarchyId === undefined) ||
              this.tariffCircuits[curIndex].id === selectedHierarchyId)) ||
          (showAllChildren === true && isIdInAnyParent)) &&
        (selectedCategory === undefined ||
          (selectedCategory === null &&
            ((this.tariffCircuits[curIndex].categories?.length ?? 0) < 1 ||
              this.tariffCircuits[curIndex].categories?.[0] === "")) ||
          this.tariffCircuits[curIndex].categories?.find(
            (searchCat) => searchCat === selectedCategory,
          ));
      return showTariffCircuit;
    });

    const sortedList = filteredList.sort((a, b) =>
      this.tariffCircuits[a].name.localeCompare(this.tariffCircuits[b].name),
    );
    return sortedList;
  }

  public makeOptionsArray(
    allIndexes: number[],
    selectedTariffCircuitIds: number[] | undefined,
    showAllChildren: boolean,
    findExactTariff?: boolean,
  ) {
    const isAllSelected =
      !isSet(selectedTariffCircuitIds) || allIndexes.length === selectedTariffCircuitIds.length;
    const optionsArray = allIndexes.map((curIndex) => {
      return {
        value: `${curIndex === SELECT_ALL_OPTION_ITEMS ? "all" : "tariff"}${curIndex}`,
        index: curIndex,
        id: this.tariffCircuits[curIndex]?.id ?? curIndex,
        label:
          curIndex === SELECT_ALL_OPTION_ITEMS
            ? allOption.label
            : this.tariffCircuits[curIndex]?.name,
        visible: true,
      } as IDropdownSelectOption;
    });

    if (!isAllSelected) {
      // loop through seletedIos
      optionsArray.forEach((curOption) => {
        const tc = this.tariffCircuits[curOption.index];
        const isSelected = selectedTariffCircuitIds?.some(
          (curSelectedId) =>
            curSelectedId === curOption.id &&
            // findExactTariff is default undefined, which is equal to true!! set to false if we do not want to find the exact tariff
            (findExactTariff !== false ||
              (showAllChildren === true &&
                isSet(tc) &&
                this.hasIdInAnyParent(curOption.index, tc))),
        );
        curOption.visible = isSelected;
      });
    }

    return optionsArray;
  }

  private hasIdInAnyParent(id: number | null | undefined, tc: ITariffCircuit): boolean {
    const tcIndex = this.tariffCircuits.indexOf(tc);
    if ((tc.id ?? tcIndex) === id) {
      return true;
    } else if (isSet(tc.parent)) {
      const aNode = this.tariffCircuits.find((item) => item.id === tc.parent);
      if (isSet(aNode)) {
        return this.hasIdInAnyParent(id, aNode);
      }
    }
    return false;
  }

  public getSelectedTimestampValues(
    trendData: ITrendData,
    curIndex: number,
    trendMember: "energy" | "power" | "costs",
    usedTimestamps: number[],
    allTimestamps: number[],
    selectedTimestamp: number | undefined,
  ) {
    let maxMaxValue = 0;
    let maxTargetValue = 0;
    let maxValue = 0;
    let selectedValue = undefined;
    let sumOfValues = 0;
    // prepare index-field for val, target-val and energy-val
    // index 0 = MaxValue
    // index 1 = TargetValue
    // index 2 = value
    const dataIndices: (keyof ITrendDataRow)[] =
      trendMember === "costs"
        ? ["costs", "targetpower", "costs"]
        : trendMember === "energy"
        ? ["energy", "targetpower", "energy"]
        : ["power", "targetpower", "power"];

    // loop through timestamps for current tariff  to find max-values and sum of all values
    usedTimestamps.forEach((curTimestamp) => {
      const matchingTimestamp =
        allTimestamps.find(
          (item) => roundTimestamp(item, false) === curTimestamp && trendData[item]?.[curIndex],
        ) ?? 0;

      // get max-value
      const trendMaxValue = trendData[matchingTimestamp]?.[curIndex]?.[dataIndices[0]] ?? 0;
      // calculate the max value of the selected time-period (only needed for cost and energy)
      sumOfValues += trendMaxValue;
      // store the maximum power (of the selected period)
      if (Math.abs(trendMaxValue) > Math.abs(maxMaxValue)) {
        maxMaxValue = trendMaxValue;
      }

      // get the target-value
      const trendTargetValue = trendData[matchingTimestamp]?.[curIndex]?.[dataIndices[1]] ?? 0;
      // store the maximum target-value (of the selected period)
      if (Math.abs(trendTargetValue) > Math.abs(maxTargetValue)) {
        maxTargetValue = trendTargetValue;
      }

      // get the value
      const trendValue = trendData[matchingTimestamp]?.[curIndex]?.[dataIndices[2]] ?? 0;
      if (Math.abs(trendValue) > Math.abs(maxValue)) {
        maxValue = trendValue;
      }
      // if only one timestamp is selected -> check if the curTimestamp is the one we are searching for
      // -> if so, store that value!
      if (isSet(selectedTimestamp) && selectedTimestamp === curTimestamp) {
        selectedValue = trendValue;
      }
    });

    // get max-value -> if a target-value was set, use this as max, otherwise the max power or energy-value
    const maxRelateValue =
      maxTargetValue > 0 ? maxTargetValue : maxMaxValue > 0 ? maxMaxValue : maxValue;

    return { selectedValue, sumOfValues, maxTargetValue, maxRelateValue };
  }
}

export default TariffCircuitList;
